import { SelectGroup, SelectItem, SelectLabel, SelectSeparator } from "@/components/ui/select";
import { firmChoices, firmStatusText, preferredIn, stateRank } from "@/lib/firms";
import type { Firm } from "@/lib/types";

/**
 * A firm picker's options for a contact in `state` (ACL-538, ADR-170 in platform): the firms that
 * take cases in the state first, in their order there, the first one said to be preferred; then every
 * other firm, as the server lists them. A firm of the state that is not active says so, since it takes
 * no new leads. Without a state, or with one no firm takes, every firm in the server's order.
 */
export function FirmOptions({ firms, state }: { firms: readonly Firm[]; state: string | null | undefined }) {
  const { state: code, inState, others } = firmChoices(firms, state);
  const preferred = code ? preferredIn(inState, code) : new Set<number>();

  const option = (firm: Firm, notes: string[] = []) => (
    <SelectItem key={firm.id} value={String(firm.id)} description={notes.join(" · ") || undefined}>
      {firm.label}
    </SelectItem>
  );

  if (!code) return <>{others.map((firm) => option(firm))}</>;
  if (inState.length === 0) {
    return (
      <SelectGroup>
        <SelectLabel>No firm takes cases in {code}</SelectLabel>
        {others.map((firm) => option(firm))}
      </SelectGroup>
    );
  }
  return (
    <>
      <SelectGroup>
        <SelectLabel>Takes cases in {code}</SelectLabel>
        {inState.map((firm) => {
          const rank = stateRank(firm, code);
          const notes = preferred.has(firm.id) ? [`Preferred in ${code}`] : rank !== null ? [`Priority ${rank}`] : [];
          if (firm.status !== "active") notes.push(firmStatusText(firm.status).label);
          return option(firm, notes);
        })}
      </SelectGroup>
      {others.length > 0 && (
        <>
          <SelectSeparator />
          <SelectGroup>
            <SelectLabel>Other firms</SelectLabel>
            {others.map((firm) => option(firm))}
          </SelectGroup>
        </>
      )}
    </>
  );
}
